import axios, { Method } from "axios"
import ApiError from "./api-error"
import { CutSurcharges, DeliveryRates, Product } from "@/types"

const apiService = async (
  endpoint: string,
  args: {
    method?: Method
    token?: string
    data?: any
    params?: any
  } = {
    method: "GET",
    data: {},
    params: {},
  },
) => {
  const { token } = args

  const config = {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: "",
    },
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  try {
    const { data } = await axios({
      url: `${process.env.NEXT_PUBLIC_API_URL || ""}/${endpoint}`,
      ...config,
      ...args,
    })

    return data
  } catch (error_: any) {
    // Timeouts, offline and DNS failures reach us with no response at all.
    const { response } = error_
    const error = new ApiError(
      response
        ? response.data.message || "Server Error"
        : "Network error. Please check your connection and try again.",
    )
    error.status = response?.status ?? 0
    error.errors = response?.data.errors ?? null
    throw error
  }
}

const API = {
  services: {
    cart: {
      get: async (token: string) => {
        return apiService(`cart`, {
          params: { token },
        })
      },
      add: async (
        token: string,
        data: { product_id: number; quantity: number; quantity_unit?: string },
      ) => {
        return apiService(`cart/create`, {
          method: "POST",
          data,
          params: { token },
        })
      },
      update: async (token: string, data: { id: number; quantity: number }) => {
        return apiService(`cart/update`, {
          method: "PUT",
          data,
          params: { token },
        })
      },
      delete: async (token: string, id: number) => {
        return apiService(`cart/delete`, {
          method: "DELETE",
          data: { id },
          params: { token },
        })
      },
      checkout: async (
        token: string,
        data: {
          shipping_address_id: string
          special_instructions: string
          reference_number: string
        },
      ) => {
        return apiService(`cart/checkout`, {
          method: "POST",
          data,
          params: { token },
        })
      },
    },
    wishlist: {
      get: async (token: string) => {
        return apiService(`wishlist`, {
          params: { token },
        })
      },
      toggle: async (token: string, data: { product_id: number }) => {
        return apiService(`wishlist/toggle`, {
          method: "POST",
          data,
          params: { token },
        })
      },
      delete: async (token: string, id: number) => {
        return apiService(`wishlist/delete`, {
          method: "DELETE",
          data: { id },
          params: { token },
        })
      },
    },
    auth: {
      login: async (data: { login: string; password: string }) => {
        return apiService("auth/login", {
          method: "POST",
          data,
        })
      },
      refreshToken: async (token: string) => {
        return apiService(`auth/refresh?token=${token}`, {
          method: "POST",
        })
      },
      requestPasswordReset: async (data: { login: string }) => {
        return apiService(`auth/password/request`, {
          method: "POST",
          data,
        })
      },
      resetPassword: async (data: {
        email: string
        token: string
        password: string
        password_confirmation: string
      }) => {
        return apiService(`auth/password/confirm`, {
          method: "POST",
          data,
        })
      },
    },
    orders: {
      index: async (token: string, page: number) => {
        return apiService(`user/orders`, {
          params: {
            token,
            page,
          },
        })
      },
      single: async (token: string, id: string) => {
        return apiService(`user/orders/${id}`, {
          params: { token },
        })
      },
    },
    user: {
      info: async (token: string) => {
        return apiService(`user/show`, {
          params: {
            token,
          },
        })
      },
      updatePassword: async (
        token: string,
        data: {
          password: string
          new_password: string
          new_password_confirmation: string
        },
      ) => {
        return apiService(`user/password`, {
          method: "PUT",
          data,
          params: { token },
        })
      },
      addresses: async (token: string) => {
        return apiService(`user/addresses`, {
          params: {
            token,
          },
        })
      },
      setDefaultAddress: async (token: string, id: string) => {
        return apiService(`user/addresses/update`, {
          method: "POST",
          data: {
            address_id: id,
          },
          params: {
            token,
          },
        })
      },
      reorder: async (
        token: string,
        data: {
          id: number
        },
      ) => {
        return apiService(`user/reorder`, {
          method: "POST",
          data,
          params: { token },
        })
      },
    },
    index: async ({
      type,
      main_category_id,
    }: {
      type: string
      main_category_id: number
    }) => {
      return apiService("index", {
        params: {
          type,
          main_category_id,
        },
      })
    },
    products: {
      index: async ({
        perPage = 39,
        page = 1,
        ...data
      }: {
        page?: number
        orderBy?: string
        orderDirection?: string
        main_category_id?: number
        sub_categories?: number[]
        component_type?: string
        fabric_type?: string
        item_numbers?: string[]
        perPage?: number
      }): Promise<{ data: Product[] }> => {
        return apiService("products", {
          params: {
            perPage,
            page,
            ...data,
          },
        })
      },
      search: async (query: string, page = 1) => {
        return apiService("search", {
          params: {
            search_term: query,
            page,
          },
        })
      },
      single: async (slug: string) => {
        return apiService(`products/${slug}`)
      },
      recommendations: async (slug: string) => {
        return apiService(`products/${slug}/recommendations`)
      },
      recentlyOrdered: async (token: string) => {
        return apiService(`products/recently-ordered`, { params: { token } })
      },
      getPrice: async (
        slug: string,
        data: { quantity: number; unit: string },
        token: string,
      ) => {
        return apiService(`products/${slug}/price`, {
          method: "GET",
          params: {
            token,
            ...data,
          },
        })
      },
      rate: async (
        token: string,
        slug: string,
        data: { rating: number; comment?: string },
      ) => {
        return apiService(`products/${slug}/rate`, {
          method: "POST",
          data,
          params: {
            token,
          },
        })
      },
    },
    categories: {
      index: async () => {
        return apiService("categories")
      },
      single: async (slug: string) => {
        return apiService(`categories/${slug}`)
      },
      subCategory: async (categorySlug: string, subCategorySlug: string) => {
        return apiService(
          `categories/${categorySlug}/subcategories/${subCategorySlug}`,
        )
      },
    },
    pricing: {
      getCutSurcharges: async (): Promise<CutSurcharges> => {
        return apiService("pricing/cut-surcharges")
      },
      getDeliveryRates: async (): Promise<DeliveryRates> => {
        return apiService("pricing/delivery-rates")
      },
    },
  },
}

export default API
